/**
 * FIXED Redux slice for FlashApplyWebApp monitoring.
 * Copy this file to FlashApplyWebApp/src/redux/monitoring.js
 *
 * Key fixes:
 * 1. Single "dashboard" API call replaces 27+ parallel Lambda invocations.
 * 2. Clean state shape that mirrors the dashboard response exactly.
 * 3. No more client-side timestamp normalization needed — the lambda handles it.
 */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { backendConnector } from "../backendIntegration/lambdas";
import logger from "../utils/logger";

export const fetchMonitoringData = createAsyncThunk(
  "monitoring/fetchData",
  async ({ env, passcode }, { rejectWithValue }) => {
    try {
      logger.log(`[Monitoring] Fetching dashboard data for env: ${env}`);

      // Single consolidated call — the lambda handles all parallel queries server-side
      const response = await backendConnector.getMetrics(
        { dashboard: "true" },
        passcode
      );

      // The lambda returns { message, data } where data is the dashboard payload
      const dashboard = response?.data || response;

      return {
        env,
        globalStats: dashboard.globalStats || {},
        health: dashboard.health || { services: {}, hosts: {} },
        snapshots: dashboard.snapshots || {
          jobsBreakdown: [],
          vectorDb: [],
          inputData: [],
        },
        atsHistory: dashboard.atsHistory || {},
        labelingHistory: dashboard.labelingHistory || [],
        recentErrors: dashboard.recentErrors || [],
        queues: dashboard.queues || {},
        summary: dashboard.summary || {},
      };
    } catch (error) {
      logger.error(`[Monitoring] Error:`, error);
      return rejectWithValue(error.message || String(error));
    }
  }
);

const envInitialState = {
  globalStats: {},
  health: { services: {}, hosts: {} },
  snapshots: { jobsBreakdown: [], vectorDb: [], inputData: [] },
  atsHistory: {},
  labelingHistory: [],
  recentErrors: [],
  queues: {},
  summary: {},
  loading: false,
  error: null,
  lastUpdated: null,
};

const initialState = {
  dev: { ...envInitialState },
  prod: { ...envInitialState },
  currentEnv: "dev",
  passcode: "",
};

const monitoringSlice = createSlice({
  name: "monitoring",
  initialState,
  reducers: {
    setEnv: (state, action) => {
      state.currentEnv = action.payload;
    },
    setPasscode: (state, action) => {
      state.passcode = action.payload;
    },
    clearMonitoringError: (state, action) => {
      const env = action.payload || state.currentEnv;
      state[env].error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMonitoringData.pending, (state, action) => {
        const { env } = action.meta.arg;
        state[env].loading = true;
        state[env].error = null;
      })
      .addCase(fetchMonitoringData.fulfilled, (state, action) => {
        const {
          env,
          globalStats,
          health,
          snapshots,
          atsHistory,
          labelingHistory,
          recentErrors,
          queues,
          summary,
        } = action.payload;
        state[env].globalStats = globalStats;
        state[env].health = health;
        state[env].snapshots = snapshots;
        state[env].atsHistory = atsHistory;
        state[env].labelingHistory = labelingHistory;
        state[env].recentErrors = recentErrors;
        state[env].queues = queues;
        state[env].summary = summary;
        state[env].loading = false;
        state[env].lastUpdated = Date.now();
      })
      .addCase(fetchMonitoringData.rejected, (state, action) => {
        const { env } = action.meta.arg;
        state[env].loading = false;
        state[env].error = action.payload;
        if (
          action.payload &&
          (action.payload.includes("401") ||
            action.payload.toLowerCase().includes("unauthorized"))
        ) {
          state.passcode = "";
        }
      });
  },
});

export const { setEnv, setPasscode, clearMonitoringError } =
  monitoringSlice.actions;
export default monitoringSlice.reducer;
